/**
 * The prefix for *most* Loop params.
 */
const LOOP_PARAM_PREFIX = 'loop_'

/**
 * Stores all `loop_*` prefixed params from the URL.
 * Cleared after the user is sent back to Loop for checkout.
 */
const LOOP_STORAGE_KEY = 'loopOnstoreParams'

/**
 * Stores the cart token and return ID separately, as recommended by Loop.
 * These persist across shopping rounds and are only updated, never cleared -
 * we have no reliable way to determine when a Loop flow is completed.
 */
const LOOP_SESSION_STORAGE_KEY = 'loopSession'

/**
 * Internal key used to track TTL of `loopOnstoreParams`.
 * Not provided by Loop, not included in `LoopParams` type.
 */
const LOOP_PARAMS_EXPIRY_KEY = '_expiresAt'

/**
 * TTL duration in MS. Refreshed on each page navigation while
 * the user is in a Loop flow.
 */
const LOOP_PARAMS_TTL_MS = 15 * 60 * 1000 // 15 minutes

/**
 * The expected value of `utm_redirect` when Loop is the source.
 */
const LOOP_UTM_REDIRECT_SOURCE = 'Loop_Returns' as const

export const LOOP_PARAM_KEYS = {
  /**
   * The ID of the Loop Return/Exchange
   */
  returnId: 'loop_return_id',
  /**
   * Currency being used for the Loop Return/Exchange
   */
  currency: 'loop_currency',
  /**
   * Only used for true "on store" Loop configs. FIGS doesn't use
   * this, but by adding it here it will be consumed.
   */
  onStoreCurrency: 'currency',
  /**
   * Total amount of credit available (base + bonus credit).
   * Value is in "cents" (13800 = $138.00 in USD).
   */
  totalCredit: 'loop_total',
  /**
   * Total amount of credit before any bonuses.
   * Value is in "cents" (13800 = $138.00 in USD).
   */
  baseCredit: 'loop_base',
  /**
   * Total amount of bonus credit (optional).
   * Value is in "cents" (13800 = $138.00 in USD).
   */
  bonusCredit: 'loop_credit',
  /**
   * The domain that is used for the shop's return portal (used when taking the user back to Loop)
   */
  domain: 'loop_domain',
  /**
   * Where to take users when they are done shopping in their return/exchange
   */
  redirectUrl: 'loop_redirect_url',
  /**
   * Always `Loop_Returns`, so we can see shoppers are being sent from Loop.
   * Can be used to confirm non-`loop_*`-prefixed params come from Loop.
   */
  utmRedirect: 'utm_redirect',
  /**
   * The cart token attached to the return/exchange in Loop.
   * Does not follow the `loop_*` prefix standard — matched explicitly.
   * Not present on initial entry; comes from the BE after Create Cart at
   * departure and is saved to `loopSession`. Loop does NOT reliably include
   * it in re-entry URLs — the saved session copy is the source of truth,
   * and this param is only a cross-check when present.
   */
  cartToken: 'token',
} as const

/**
 * The subset of LOOP_PARAM_KEYS that are stored in `loopOnstoreParams`.
 *
 * `cartToken` and `returnId` are excluded — they live under `loopSession`.
 */
const LOOP_ONSTORE_PARAM_KEYS = {
  currency: LOOP_PARAM_KEYS.currency,
  totalCredit: LOOP_PARAM_KEYS.totalCredit,
  baseCredit: LOOP_PARAM_KEYS.baseCredit,
  bonusCredit: LOOP_PARAM_KEYS.bonusCredit,
  domain: LOOP_PARAM_KEYS.domain,
  redirectUrl: LOOP_PARAM_KEYS.redirectUrl,
  utmRedirect: LOOP_PARAM_KEYS.utmRedirect,
} as const

export type LoopParams = Record<
  (typeof LOOP_ONSTORE_PARAM_KEYS)[keyof typeof LOOP_ONSTORE_PARAM_KEYS],
  string | null
>

type LoopSession = {
  token: string | null
  loop_return_id: string
  /**
   * Set to `true` by `LoopProvider` when a returning shopper re-enters with a matching `loop_return_id`.
   * Signals to `useCartStore` that it should sync the Loop cart into the Shopify cart on initialization.
   * Cleared by `useCartStore` after the sync is complete.
   */
  syncRequired: boolean
  /**
   * Set to `true` once the Loop flow's cart has been cleared/created, so reloads don't re-clear.
   */
  cartInitialized: boolean
}

/**
 * The subset of `LOOP_PARAM_KEYS` values that do not share the `loop_` prefix
 * and must be matched explicitly in `LoopProvider`.
 */
const LOOP_PARAM_KEYS_EXPLICIT = new Set<string>([
  LOOP_PARAM_KEYS.utmRedirect,
  LOOP_PARAM_KEYS.cartToken,
  LOOP_PARAM_KEYS.onStoreCurrency,
])

/**
 * Returns true if a given query param key belongs to the Loop param set,
 * either by prefix or explicit match.
 */
export function isLoopParam(key: string): boolean {
  return key.startsWith(LOOP_PARAM_PREFIX) || LOOP_PARAM_KEYS_EXPLICIT.has(key)
}

/**
 * Write Loop query parameters to `localStorage`.
 *
 * Called by `LoopProvider` when Loop query params are detected on entry.
 *
 * Note: `token` and `returnId` are intentionally excluded - they are handled
 * via `setLoopSession`.
 */
export function setLoopParams(params: Record<string, string>): void {
  const {
    [LOOP_PARAM_KEYS.cartToken]: _token,
    [LOOP_PARAM_KEYS.returnId]: _returnId,
    ...rest
  } = params
  localStorage.setItem(
    LOOP_STORAGE_KEY,
    JSON.stringify({ ...rest, [LOOP_PARAMS_EXPIRY_KEY]: Date.now() + LOOP_PARAMS_TTL_MS })
  )
}

/**
 * Reads the Loop params object from `localStorage`.
 *
 * Used by `CartContainer` and `useCartStore` to determine if the user is in a Loop flow.
 */
export function getLoopParams(): LoopParams {
  try {
    const stored = localStorage.getItem(LOOP_STORAGE_KEY)
    const parsed = stored ? JSON.parse(stored) : {}
    return Object.fromEntries(
      Object.values(LOOP_ONSTORE_PARAM_KEYS).map(key => [key, parsed[key] ?? null])
    ) as LoopParams
  } catch {
    return Object.fromEntries(
      Object.values(LOOP_ONSTORE_PARAM_KEYS).map(key => [key, null])
    ) as LoopParams
  }
}

/**
 * Writes the cart token and return ID to a separate `localStorage` entry.
 *
 * Called by `useCartStore` after the BE returns a token from Cart Create,
 * and by `LoopProvider` on re-entry when a token is present in the URL.
 */
export function setLoopSession(session: LoopSession): void {
  localStorage.setItem(LOOP_SESSION_STORAGE_KEY, JSON.stringify(session))
}

/**
 * Reads the Loop session from `localStorage`.
 */
export function getLoopSession(): LoopSession | null {
  try {
    const stored = localStorage.getItem(LOOP_SESSION_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

/**
 * Clears Loop params from `localStorage` under the `LOOP_STORAGE_KEY`.
 *
 * Does not touch params under the `LOOP_SESSION_KEY`, as those values
 * persist between user re-entries.
 */
export function clearLoopParams(): void {
  localStorage.removeItem(LOOP_STORAGE_KEY)
}

/**
 * Checks if a set of params are valid Loop params.
 *
 * Accepts params as input since this helper can be used to determine when
 * a user is entering a Loop flow.
 */
export function areLoopParamsValid(params: LoopParams | Record<string, string>): boolean {
  // NOTE: using `.some()` as all but two Loop params have the prefix.
  const hasLoopPrefixedParams = Object.keys(params).some(key => key.startsWith(LOOP_PARAM_PREFIX))

  // We check for the `totalCredit` param based on Loop's docs,
  // but this could be expanded to check for all but a subset of optional params.
  const hasLoopTotalCredit = !!params[LOOP_PARAM_KEYS.totalCredit]
  const hasValidUtmRedirect = params[LOOP_PARAM_KEYS.utmRedirect] === LOOP_UTM_REDIRECT_SOURCE
  return hasLoopPrefixedParams && hasLoopTotalCredit && hasValidUtmRedirect
}

/**
 * Refreshes the TTL for `loopOnstoreParams`.
 *
 * Called by `LoopProvider` on each page navigation while
 * the user is in a Loop flow.
 */
export function refreshLoopParamsTTL(): void {
  try {
    const stored = localStorage.getItem(LOOP_STORAGE_KEY)
    if (!stored) return
    const parsed = JSON.parse(stored)
    localStorage.setItem(
      LOOP_STORAGE_KEY,
      JSON.stringify({ ...parsed, [LOOP_PARAMS_EXPIRY_KEY]: Date.now() + LOOP_PARAMS_TTL_MS })
    )
  } catch {
    // if we can't refresh, then the previous expiry timestamp remains in effect and will eventually be exceeded
  }
}

/**
 * Returns `true` if the Loop params TTL has been exceeded.
 * A missing or unparseable expiry is treated as expired.
 */
export function areLoopParamsExpired(): boolean {
  try {
    const stored = localStorage.getItem(LOOP_STORAGE_KEY)
    if (!stored) return true
    const parsed = JSON.parse(stored)
    const expiresAt = parsed[LOOP_PARAMS_EXPIRY_KEY]
    if (typeof expiresAt !== 'number') return true
    return Date.now() > expiresAt
  } catch {
    return true
  }
}
